import { matchPath, generatePath } from 'react-router-dom';
import { get, isNil, keys, values, keyBy, identity } from 'lodash';
import { reverse } from 'lodash/array';
import { FORM_MODES } from 'config/formModes';
import { ACCOUNT_ID } from 'hooks/queryParams/useSelectedAccountId';
import { SESSION_STORAGE_KEYS } from 'modules/codeless-events/consts/iframeParentConstants';
import { authTokenManager } from 'modules/taboola-common-frontend-modules/authentication';
import { wizardStepIdValueStore } from 'modules/taboola-common-frontend-modules/formData/utils/wizardStepValueStore';
import {
    ENTITY_PARAM_TEMPLATE,
    FORM_MODE_TO_PATH_MAP,
    MODE_PARAM_TEMPLATE,
    MODULE_TEMPLATE,
    PATH_TO_FORM_MODE_MAP,
} from '../../../../config/routes';
import { ACCOUNT_PARAM_TEMPLATE } from '../../../campaigns/config/routes/account';
import { SEARCH_TERM } from '../../../campaigns/modules/campaigns-reports/hooks/useSearchTerm';
import { parseQueryParams } from '../../query-params';
import { agentNameValueStore } from './agentNameValueStore';
import { chatClientValueStore } from './chatClientValueStore';
import { chatSessionIdValueStore } from './chatSessionIdValueStore';

export const ENTITY_ID_PARAM_NAME = 'entityId';
export const ENTITY_ID_PARAM_TEMPLATE = `/:${ENTITY_ID_PARAM_NAME}?`;
export const PARENT_ENTITY_ID_PARAM_NAME = 'parentEntityId';
export const PARENT_ENTITY_ID_PARAM_TEMPLATE = `/:${PARENT_ENTITY_ID_PARAM_NAME}?`;
const BASE_FORM_ROUTE_PATH = `${MODULE_TEMPLATE}?${ENTITY_PARAM_TEMPLATE}?${MODE_PARAM_TEMPLATE}?`;
export const FORM_ROUTE_PATH = `${BASE_FORM_ROUTE_PATH}${ACCOUNT_PARAM_TEMPLATE}${PARENT_ENTITY_ID_PARAM_TEMPLATE}${ENTITY_ID_PARAM_TEMPLATE}`;

const PARAMS_TO_CONTEXT_MAP = {
    [ACCOUNT_ID]: {
        queryParamName: ACCOUNT_ID,
        contextKey: 'accountId',
        toQueryParamValue: (accountName, { accountMap }) => {
            const accountId = values(accountMap).find(account => account.accountName === accountName)?.accountId;

            return accountId;
        },
        toContextValue: (accountId, { accountMap }) => get(accountMap, `${accountId}.accountName`, accountId),
    },
    [SEARCH_TERM]: {
        queryParamName: SEARCH_TERM,
        contextKey: 'reportSearch',
        toQueryParamValue: identity,
        toContextValue: identity,
    },
};

const CONTEXT_TO_PARAMS_MAP = keyBy(PARAMS_TO_CONTEXT_MAP, 'contextKey');

export const getUrl = ({ appContext = {}, formContext = {} } = {}, accountMap) => {
    if (formContext.mode) {
        const contextForUrl = { ...appContext, ...formContext };
        const [entityId, parentEntityId] = reverse((formContext.entityId ?? '').split('/'));
        contextForUrl.entityId = entityId;
        contextForUrl.parentEntityId = parentEntityId;

        try {
            if (formContext.mode === FORM_MODES.CREATE) {
                return generatePath(BASE_FORM_ROUTE_PATH, {
                    ...contextForUrl,
                    mode: FORM_MODE_TO_PATH_MAP[contextForUrl.mode],
                });
            }

            const url = generatePath(FORM_ROUTE_PATH, {
                ...contextForUrl,
                mode: FORM_MODE_TO_PATH_MAP[contextForUrl.mode],
            });
            return url;
        } catch (error) {
            return '';
        }
    }

    const queryParams = `${keys(appContext)
        .filter(contextKey => {
            return CONTEXT_TO_PARAMS_MAP[contextKey] && !isNil(appContext[contextKey]);
        })
        .map(contextKey => {
            const config = CONTEXT_TO_PARAMS_MAP[contextKey];
            const queryParamName = config.queryParamName;
            return `${queryParamName}=${config.toQueryParamValue(appContext[contextKey], {
                accountMap,
                context: appContext,
            })}`;
        })
        .join('&')}`;

    if (!queryParams && appContext.metadata?.currentUrl) {
        return appContext.metadata.currentUrl;
    }

    return queryParams ? `?${queryParams}` : '';
};

export const getContext = ({ url, userId, accountMap, accountName, globalAccountName, campaignId, promptVersion }) => {
    const metadata = { currentUrl: url };
    const [path, search] = url.split('?');
    const match = matchPath(path, FORM_ROUTE_PATH);
    const matchParams = get(match, 'params', {});
    const { module, entity, entityId } = matchParams;
    const queryParams = parseQueryParams(search);
    const isForm = get(matchParams, 'mode');

    const chatContext = {
        sessionId: chatSessionIdValueStore.getValue(),
        client: chatClientValueStore.getValue(),
        promptVersion,
        messageId: '',
        agentName: agentNameValueStore.getValue(),
    };

    const contextFromQueryParams = keys(queryParams).reduce((acc, queryParamName) => {
        const config = PARAMS_TO_CONTEXT_MAP[queryParamName];
        if (config) {
            acc[config.contextKey] = config.toContextValue(queryParams[queryParamName], {
                accountMap,
                queryParams,
            });
        }
        return acc;
    }, {});

    const selectedAccountId = globalAccountName || authTokenManager.storage?.getItem(SESSION_STORAGE_KEYS.ACCOUNT_ID);
    const appContext = { module, campaignId, userId, metadata, accountId: selectedAccountId };

    if (!isForm) {
        return {
            appContext,
            chatContext,
            formContext: null,
        };
    }

    const accountIdContextKey = PARAMS_TO_CONTEXT_MAP[ACCOUNT_ID].contextKey;
    const accountId = matchParams.accountId || accountName || contextFromQueryParams[accountIdContextKey];
    const mode = PATH_TO_FORM_MODE_MAP[matchParams.mode];
    const formContext = { accountId, mode, entity, entityId, step: wizardStepIdValueStore.getValue() };

    if (mode !== FORM_MODES.CREATE && !entityId) {
        return {
            appContext,
            chatContext,
            formContext: {
                ...formContext,
                entityId: matchParams.parentEntityId,
            },
        };
    }

    return {
        appContext,
        chatContext,
        formContext,
    };
};
